import type { ReactNode } from "react";

export interface SectionCardProps {
  /** Anchor id — enables `#hash` deep-linking / scroll. */
  id?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** Right-slot: segmented controls, action buttons, New-Soon badge, etc. */
  right?: ReactNode;
  /** When true, renders a small amber dot next to the title. */
  isDirty?: boolean;
  /** When true, dims the whole card (used for "(New Soon)" placeholder sections). */
  disabled?: boolean;
  /**
   * Visual variant. "default" keeps the bordered-header card used across the
   * app. "flat" is the Trioty look — a 20px-radius card with a borderless
   * header, a neutral (non-accent) icon tile, and padding-integrated spacing.
   */
  variant?: "default" | "flat";
  children: ReactNode;
  className?: string;
}

/**
 * The primary card surface used across the redesigned pages. Renders a
 * rounded white surface with a bordered header row (icon tile + title +
 * subtitle + right-slot) and a body slot.
 *
 * Always-expanded (no collapse affordance). Use the `id` prop for
 * deep-link scrolling. For sections gated behind "(New Soon)", pass the
 * `NewSoonBadge` into the `right` slot and set `disabled` to dim the body.
 */
export function SectionCard({
  id,
  title,
  subtitle,
  icon,
  right,
  isDirty = false,
  disabled = false,
  variant = "default",
  children,
  className = "",
}: SectionCardProps) {
  const flat = variant === "flat";
  return (
    <section
      id={id}
      data-ui="card"
      data-variant={variant}
      className={[
        flat ? "rounded-[20px]" : "rounded-[var(--radius-card,16px)]",
        "border-[length:var(--border-card-width,1px)] border-[color:var(--border-light)] bg-[color:var(--surface)]",
        "shadow-[var(--shadow-card,0_1px_2px_rgba(15,20,40,0.04))]",
        disabled ? "opacity-70" : "",
        className,
      ].join(" ")}
    >
      <header
        data-ui="card-header"
        className={
          flat
            ? "flex items-start gap-[11px] px-[18px] pb-[15px] pt-[18px]"
            : "flex items-center gap-2.5 border-b-[length:var(--border-card-width,1px)] border-[color:var(--border-light)] [border-bottom-style:var(--divider-style,solid)] px-[18px] py-3"
        }
      >
        {icon ? (
          <span
            aria-hidden="true"
            data-ui="card-icon"
            className={
              flat
                ? "inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[var(--radius-tile,9px)] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-primary)]"
                : "inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md bg-[color-mix(in_oklab,var(--accent-500)_14%,white)] text-[color:var(--accent-600)]"
            }
          >
            {icon}
          </span>
        ) : null}
        <div className="flex min-w-0 flex-col">
          <span
            data-ui="card-title"
            className={[
              "ui-display flex items-center gap-2 leading-tight tracking-tight text-[color:var(--text-primary)]",
              flat ? "text-[14.5px]" : "text-card-title font-semibold",
            ].join(" ")}
          >
            {/* The title WRAPS; it used to `truncate`.
                A card whose header carries a right-slot control (a segmented
                switch, a disclosure button) gives its title whatever width is
                left, and on a 390px screen that clipped real titles mid-word:
                "Where the mone…", "For your account…". A heading that has to
                be guessed at is worth less than a two-line heading, which is
                the same call `StatCard`'s label already made for the same
                reason — and Arabic runs longer than the English these headers
                were measured against, so the clip landed hardest in the
                primary language. Rows stretch, so a wrapped title costs a card
                some height and costs nothing in alignment. */}
            <span className="min-w-0 break-words">{title}</span>
            {isDirty ? (
              <span
                aria-hidden="true"
                className="h-2 w-2 shrink-0 rounded-full bg-[color:var(--accent-500)]"
              />
            ) : null}
          </span>
          {subtitle ? (
            <span
              data-ui="card-subtitle"
              className={[
                // Wraps, like the title, instead of `truncate`: settings
                // subtitles ("الدفع عند الاستلام…", "أسعار التوصيل…") were cut
                // on their FIRST line on a phone. Three lines max — at two,
                // the getting-started body still lost its point ("…خطوات
                // اختيارية") on a 412 px screen; only a fourth line clips.
                "line-clamp-3 break-words text-[color:var(--text-muted)]",
                flat ? "mt-px text-[12px]" : "text-meta",
              ].join(" ")}
            >
              {subtitle}
            </span>
          ) : null}
        </div>
        {right ? (
          <div data-ui="card-actions" className="ms-auto flex shrink-0 items-center gap-2">{right}</div>
        ) : null}
      </header>
      <div
        data-ui="card-body"
        className={[
          /*
           * `--card-pad-x` is the body's inline padding, published as a
           * variable so a child that bleeds to the card's edges (a table, a
           * full-width list) can cancel it EXACTLY with
           * `-mx-[var(--card-pad-x)]`. It used to be copied as a literal
           * `-mx-[18px]`, which was right for this default and 2px wrong under
           * any look that pads the body differently — the looks use 16px — so
           * a bleeding table overshot into the card's border and painted its
           * header band over it. Looks override the variable, not the padding.
           */
          flat
            ? "[--card-pad-x:18px] px-[var(--card-pad-x)] pb-5 pt-0"
            : "[--card-pad-x:18px] px-[var(--card-pad-x)] py-[18px]",
          disabled ? "pointer-events-none" : "",
        ].join(" ")}
      >
        {children}
      </div>
    </section>
  );
}
